/** Root-element attributes that make a block the target of an in-page `#anchorId` link. */
export interface BlockAnchorAttributes {
  id?: string;
  /** Marks the element as an authored anchor so the theme offsets it below the fixed nav. */
  'data-poly-anchor'?: '';
}

/**
 * Normalises an authored anchor id to letters, numbers and dashes so it always matches a
 * `#id` link: surrounding whitespace and a leading `#` are removed, inner whitespace runs
 * become a dash, and every other character is dropped (`"#Bank account FAQ!"` becomes
 * `Bank-account-FAQ`). Uniqueness on the page is a content concern and is not enforced here.
 *
 * @param anchorId - Authored anchor id, or `undefined` when the field was left blank.
 * @returns The normalised id, or `''` when nothing usable remains.
 */
export const normalizeAnchorId = (anchorId?: string): string =>
  (anchorId ?? '')
    .trim()
    .replace(/^#+/, '')
    .replace(/\s+/g, '-')
    .replace(/[^A-Za-z0-9-]/g, '');

/**
 * Attributes for a block's root element from its authored `anchorId`. Shared by every block
 * that carries an Anchor ID field so normalisation and the scroll-offset hook stay identical.
 * The id is normalised rather than rejected (see `normalizeAnchorId`); only an authored value
 * that normalises to nothing is dropped, with a dev warning.
 *
 * @param anchorId - Authored anchor id, or `undefined` when the field was left blank.
 * @returns `{ id, 'data-poly-anchor' }` for a usable id, otherwise `{}`.
 */
export const blockAnchorAttributes = (anchorId?: string): BlockAnchorAttributes => {
  const id = normalizeAnchorId(anchorId);
  if (!id) {
    if (anchorId?.trim() && process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console -- authoring diagnostic for an unusable anchor id
      console.warn(
        `[Poly] Anchor ID "${anchorId}" is not used: it has no letters, numbers or dashes.`,
      );
    }
    return {};
  }
  return { id, 'data-poly-anchor': '' };
};
